import React, { useEffect, useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";

import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";
import InitialsAvatar from "../../util/name_initial";
import OfficialAssignmentGameCard from "./OfficialAssignmentGameCard";

const normalizeEventId = (eventId) => {
    const value = String(eventId ?? "");
    if (/^\d+$/.test(value)) return value;

    try {
        const decoded = window.atob(value);
        const parts = decoded.split(":");
        return parts[parts.length - 1] || value;
    } catch {
        return value;
    }
};

const INVITE_EVENT_REFEREES_BULK = gql`
    mutation InviteEventRefereesBulk($invitations: [EventRefereeInvitationInput!]!) {
        inviteEventRefereesBulk(invitations: $invitations) {
            success
            message
            assignments {
                id
                status
                referee {
                    id
                    fullName
                }
            }
            results {
                eventId
                refereeId
                success
                message
            }
            partialSuccess
            succeededCount
            failedCount
        }
    }
`;

const GET_EVENT_REFEREE_ASSIGNMENTS = gql`
    query GetEventRefereeAssignments($eventIds: [ID!]!) {
        eventRefereeAssignmentsForEvents(eventIds: $eventIds) {
            id
            event { id }
            status
            referee { id }
        }
    }
`;

const GET_ELIGIBLE_REFEREES_FOR_EVENTS = gql`
    query GetEligibleRefereesForEvents($eventIds: [ID!]!) {
        eligibleRefereesForEvents(eventIds: $eventIds) {
            eventId
            referees { id fullName }
            ineligibleReferees { referee { id fullName } reasons }
        }
    }
`;

const REPLACE_EVENT_REFEREE = gql`
    mutation ReplaceEventReferee($eventId: ID!, $refereeId: Int!) {
        replaceEventReferee(eventId: $eventId, refereeId: $refereeId) {
            success
            message
            assignment { id status referee { id fullName } }
        }
    }
`;

const CANCEL_EVENT_REFEREE_ASSIGNMENT = gql`
    mutation CancelEventRefereeAssignment($assignmentId: Int!) {
        cancelEventRefereeAssignment(assignmentId: $assignmentId) {
            success
            message
            assignment { id status referee { id fullName } }
        }
    }
`;

export default function SmartOfficialAssignmentModal({
    modalId = "smart-official-assignment-modal",
    selectedEvents = [],
    onInvited,
}) {
    const { t, language } = useTranslation();
    const colors = useThemeColors();
    const [inviteEventReferees, { loading }] = useMutation(INVITE_EVENT_REFEREES_BULK);
    const [replaceEventReferee, { loading: replacing }] = useMutation(REPLACE_EVENT_REFEREE);
    const [cancelEventRefereeAssignment, { loading: cancelling }] = useMutation(CANCEL_EVENT_REFEREE_ASSIGNMENT);
    const [selections, setSelections] = useState({});
    const [initialSelections, setInitialSelections] = useState({});
    const [bulkOfficialIds, setBulkOfficialIds] = useState([]);
    const [bulkOfficialSearch, setBulkOfficialSearch] = useState("");
    const [lastBulkAdded, setLastBulkAdded] = useState({});
    const [resendingKey, setResendingKey] = useState(null);
    const [replacementTarget, setReplacementTarget] = useState(null);

    const selectedGames = useMemo(
        () => selectedEvents.filter((event) => event?.eventType === "GAME"),
        [selectedEvents]
    );
    const { data: assignmentData, loading: assignmentsLoading, refetch: refetchAssignments } = useQuery(GET_EVENT_REFEREE_ASSIGNMENTS, {
        variables: { eventIds: selectedGames.map((event) => event.id) },
        skip: selectedGames.length === 0,
        fetchPolicy: "network-only",
    });
    const { data: eligibilityData, loading: eligibilityLoading } = useQuery(GET_ELIGIBLE_REFEREES_FOR_EVENTS, {
        variables: { eventIds: selectedGames.map((event) => event.id).filter(Boolean) },
        skip: selectedGames.length === 0,
        fetchPolicy: "network-only",
    });
    const eligibleRefereesByEvent = useMemo(() => {
        const result = {};
        (eligibilityData?.eligibleRefereesForEvents || []).forEach((entry) => {
            result[normalizeEventId(entry.eventId)] = entry.referees || [];
        });
        return result;
    }, [eligibilityData]);
    const ineligibleRefereesByEvent = useMemo(() => {
        const result = {};
        (eligibilityData?.eligibleRefereesForEvents || []).forEach((entry) => {
            result[normalizeEventId(entry.eventId)] = entry.ineligibleReferees || [];
        });
        return result;
    }, [eligibilityData]);
    const assignmentsByEvent = useMemo(() => {
        const result = {};
        (assignmentData?.eventRefereeAssignmentsForEvents || []).forEach((assignment) => {
            result[`${assignment.event.id}:${assignment.referee.id}`] = assignment;
        });
        return result;
    }, [assignmentData]);
    const bulkOfficials = useMemo(() => {
        const officials = new Map();
        selectedGames.forEach((event) => {
            const eventKey = normalizeEventId(event.id);
            [...(eligibleRefereesByEvent[eventKey] || []), ...(event.responsibles || [])].forEach((official) => {
                if (official?.id != null && !officials.has(String(official.id))) {
                    officials.set(String(official.id), official);
                }
            });
        });
        return [...officials.values()].sort((first, second) => (first.fullName || first.name || "").localeCompare(second.fullName || second.name || ""));
    }, [eligibleRefereesByEvent, selectedGames]);
    const conflictingSelectionKeys = useMemo(() => {
        const selectionsByDate = {};
        selectedGames.forEach((event) => {
            if (!event.date) return;
            (selections[event.id] || []).forEach((refereeId) => {
                const key = `${event.date}:${refereeId}`;
                selectionsByDate[key] = [...(selectionsByDate[key] || []), event.id];
            });
        });
        return new Set(Object.entries(selectionsByDate)
            .filter(([, eventIds]) => eventIds.length > 1)
            .map(([key]) => key));
    }, [selectedGames, selections]);
    const plannedInvitations = useMemo(() => selectedGames.flatMap((event) => (selections[event.id] || [])
        .filter((refereeId) => !(initialSelections[event.id] || []).includes(refereeId))
        .map((refereeId) => ({eventId: event.id, refereeId}))), [selectedGames, selections, initialSelections]);
    const conflictingInvitationCount = useMemo(() => plannedInvitations.filter(({eventId, refereeId}) => {
        const event = selectedGames.find((candidate) => String(candidate.id) === String(eventId));
        return event?.date && conflictingSelectionKeys.has(`${event.date}:${refereeId}`);
    }).length, [conflictingSelectionKeys, plannedInvitations, selectedGames]);
    const skippedBulkOfficialCount = useMemo(() => selectedGames.reduce((count, event) => {
        const eligibleIds = new Set((eligibleRefereesByEvent[normalizeEventId(event.id)] || []).map((official) => String(official.id)));
        return count + bulkOfficialIds.filter((officialId) => !eligibleIds.has(String(officialId))).length;
    }, 0), [bulkOfficialIds, eligibleRefereesByEvent, selectedGames]);

    useEffect(() => {
        const nextSelections = {};
        selectedGames.forEach((event) => {
            nextSelections[event.id] = (event.responsibles || []).map((referee) => String(referee.id));
        });
        setSelections(nextSelections);
        setInitialSelections(nextSelections);
        setBulkOfficialIds([]);
        setBulkOfficialSearch("");
        setLastBulkAdded({});
    }, [selectedGames]);

    const filteredBulkOfficials = useMemo(() => {
        const search = bulkOfficialSearch.trim().toLowerCase();
        if (!search) return bulkOfficials;
        return bulkOfficials.filter((official) => (official.fullName || official.name || "").toLowerCase().includes(search));
    }, [bulkOfficialSearch, bulkOfficials]);

    const toggleBulkOfficial = (officialId) => {
        setBulkOfficialIds((current) => current.includes(officialId) ? current.filter((id) => id !== officialId) : [...current, officialId]);
    };

    const applyBulkOfficials = () => {
        if (!bulkOfficialIds.length) {
            toast.info(t("officialAssignmentModal.messages.selectBulkOfficial"));
            return;
        }
        const addedByEvent = {};
        setSelections((current) => {
            const nextSelections = {...current};
            selectedGames.forEach((event) => {
                const eligibleIds = new Set((eligibleRefereesByEvent[String(event.id)] || []).map((official) => String(official.id)));
                const currentIds = new Set(nextSelections[event.id] || []);
                (event.responsibles || []).forEach((official) => currentIds.add(String(official.id)));
                const addedIds = [];
                bulkOfficialIds.forEach((officialId) => {
                    if (eligibleIds.has(String(officialId)) && !currentIds.has(String(officialId))) {
                        currentIds.add(String(officialId));
                        addedIds.push(String(officialId));
                    }
                });
                if (addedIds.length) addedByEvent[event.id] = addedIds;
                nextSelections[event.id] = [...currentIds];
            });
            return nextSelections;
        });
        setLastBulkAdded(addedByEvent);
        toast.success(t("officialAssignmentModal.messages.bulkApplied"));
    };

    const undoBulkOfficials = () => {
        setSelections((current) => Object.entries(lastBulkAdded).reduce((nextSelections, [eventId, addedIds]) => ({
            ...nextSelections,
            [eventId]: (nextSelections[eventId] || []).filter((officialId) => !addedIds.includes(officialId)),
        }), {...current}));
        setLastBulkAdded({});
        setBulkOfficialIds([]);
    };

    const toggleSelection = (eventId, refereeId) => {
        setSelections((current) => {
            const selected = current[eventId] || [];
            const value = String(refereeId);
            return {
                ...current,
                [eventId]: selected.includes(value)
                    ? selected.filter((id) => id !== value)
                    : [...selected, value],
            };
        });
    };

    const handleInvite = async () => {
        const invitations = selectedGames
            .map((event) => ({
                eventId: event.id,
                refereeIds: (selections[event.id] || [])
                    .filter((refereeId) => !(initialSelections[event.id] || []).includes(refereeId))
                    .map((refereeId) => Number(refereeId)),
            }))
            .filter((invitation) => invitation.refereeIds.length > 0);

        if (!invitations.length) {
            toast.info(t("officialAssignmentModal.messages.selectReferee"));
            return;
        }

        try {
            const result = await inviteEventReferees({variables: {invitations}});
            const payload = result.data?.inviteEventRefereesBulk;
            if (!payload?.success) {
                toast.error(payload?.message || t("officialAssignmentModal.messages.failed"));
                return;
            }

            const successfulInvitations = (payload.results || []).filter((invitation) => invitation.success);
            if (successfulInvitations.length) {
                setInitialSelections((current) => successfulInvitations.reduce((nextSelections, invitation) => ({
                    ...nextSelections,
                    [invitation.eventId]: [...new Set([...(nextSelections[invitation.eventId] || []), String(invitation.refereeId)])],
                }), {...current}));
            }
            if (payload.partialSuccess) {
                toast.info(t("officialAssignmentModal.messages.partialSent", {count: payload.failedCount}));
            } else {
                toast.success(t("officialAssignmentModal.messages.sent"));
            }
            await refetchAssignments();
            await onInvited?.();
            if (!payload.partialSuccess && window.$ && typeof window.$(`#${modalId}`).modal === "function") {
                window.$(`#${modalId}`).modal("hide");
            }
        } catch (error) {
            toast.error(getLocalizedGraphQLError(error, t, "officialAssignmentModal.messages.failed"));
        }
    };

    const handleResend = async (event, refereeId) => {
        const key = `${event.id}:${refereeId}`;
        setResendingKey(key);
        try {
            const result = await inviteEventReferees({
                variables: {
                    invitations: [{eventId: event.id, refereeIds: [Number(refereeId)]}],
                },
            });
            if (!result.data?.inviteEventRefereesBulk?.success) {
                toast.error(result.data?.inviteEventRefereesBulk?.message || t("officialAssignmentModal.messages.failed"));
                return;
            }
            toast.success(t("officialAssignmentModal.messages.resent"));
            await refetchAssignments();
            onInvited?.();
        } catch (error) {
            toast.error(getLocalizedGraphQLError(error, t, "officialAssignmentModal.messages.failed"));
        } finally {
            setResendingKey(null);
        }
    };

    const handleCancel = async (assignmentId) => {
        if (!assignmentId) return;
        try {
            const result = await cancelEventRefereeAssignment({variables: {assignmentId: Number(assignmentId)}});
            if (!result.data?.cancelEventRefereeAssignment?.success) {
                toast.error(result.data?.cancelEventRefereeAssignment?.message || t("officialAssignmentModal.messages.failed"));
                return;
            }
            toast.success(t("assignmentsPage.assignmentCancelled"));
            await refetchAssignments();
            onInvited?.();
        } catch (error) {
            toast.error(getLocalizedGraphQLError(error, t, "officialAssignmentModal.messages.failed"));
        }
    };

    const handleReplace = async () => {
        if (!replacementTarget) return;
        const selectedRefereeId = (selections[replacementTarget.eventId] || [])
            .find((refereeId) => {
                if (refereeId === replacementTarget.refereeId) return false;
                const status = assignmentsByEvent[`${replacementTarget.eventId}:${refereeId}`]?.status?.toLowerCase();
                return !["accepted", "completed"].includes(status);
            });
        if (!selectedRefereeId) {
            toast.info(t("officialAssignmentModal.messages.selectReplacement"));
            return;
        }
        try {
            const result = await replaceEventReferee({
                variables: {
                    eventId: replacementTarget.eventId,
                    refereeId: Number(selectedRefereeId),
                },
            });
            if (!result.data?.replaceEventReferee?.success) {
                toast.error(result.data?.replaceEventReferee?.message || t("officialAssignmentModal.messages.failed"));
                return;
            }
            toast.success(t("officialAssignmentModal.messages.replaced"));
            setReplacementTarget(null);
            await refetchAssignments();
            onInvited?.();
        } catch (error) {
            toast.error(getLocalizedGraphQLError(error, t, "officialAssignmentModal.messages.failed"));
        }
    };

    const ui = {
        shell: { borderRadius: 18, overflow: "hidden", border: "1px solid rgba(0,0,0,.08)", boxShadow: "0 24px 70px rgba(0,0,0,.18)" },
        card: { border: "1px solid rgba(0,0,0,.08)", borderRadius: 14, padding: 16, background: "#fff", boxShadow: "0 8px 22px rgba(0,0,0,.05)" },
    };

    return (
        <div className="modal fade kbola-modal" id={modalId} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                <div className="modal-content kbola-modalContent official-assignment-modal" style={ui.shell}>
                    <div className="official-assignment-hero" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary || colors.primary})`, color: colors.textOnPrimary }}>
                        <div className="official-assignment-hero-icon"><i className="fas fa-user-check" /></div>
                        <div className="official-assignment-hero-copy">
                            <span className="official-assignment-eyebrow">{t("officialAssignmentModal.badge")}</span>
                            <h4 className="modal-title">{t("officialAssignmentModal.title")}</h4>
                            <p>
                                {t("officialAssignmentModal.subtitle")}
                                {selectedGames.length > 0 && ` · ${selectedGames.length} ${t("officialAssignmentModal.labels.games")}`}
                            </p>
                        </div>
                        <button type="button" className="official-assignment-close" data-dismiss="modal" aria-label={t("officialAssignmentModal.close")}>
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div className="modal-body kbola-modalBody official-assignment-body">
                        <div className="official-assignment-intro">
                            <i className="fas fa-shield-alt" />
                            <span>{t("officialAssignmentModal.labels.stagePoolHint")}</span>
                        </div>
                        {!selectedGames.length ? (
                            <div className="alert alert-light border">{t("officialAssignmentModal.messages.selectGame")}</div>
                        ) : (
                            <>
                                <div className="official-assignment-bulk-panel">
                                    <div className="official-assignment-bulk-copy">
                                        <div className="official-assignment-picker-label"><i className="fas fa-layer-group mr-2" style={{ color: colors.primary }} />{t("officialAssignmentModal.labels.bulkOfficials")}</div>
                                        <p>{t("officialAssignmentModal.labels.bulkOfficialsHint")}</p>
                                    </div>
                                    <div className="official-assignment-bulk-picker">
                                        <div className="official-assignment-bulk-search-row">
                                            <div className="official-assignment-bulk-search">
                                                <i className="fas fa-search" aria-hidden="true" />
                                                <input type="search" value={bulkOfficialSearch} onChange={(event) => setBulkOfficialSearch(event.target.value)} placeholder={t("officialAssignmentModal.labels.searchOfficials")} aria-label={t("officialAssignmentModal.labels.searchOfficials")} disabled={loading || replacing || cancelling} />
                                            </div>
                                            <button type="button" className="official-assignment-bulk-link is-muted" onClick={() => setBulkOfficialIds([])} disabled={!bulkOfficialIds.length || loading || replacing || cancelling}>{t("officialAssignmentModal.labels.clearSelected")}</button>
                                        </div>
                                        {bulkOfficials.length > 0 && <div className="official-assignment-bulk-results">
                                            {filteredBulkOfficials.length ? filteredBulkOfficials.map((official) => {
                                                const officialId = String(official.id);
                                                const isSelected = bulkOfficialIds.includes(officialId);
                                                return (
                                                    <button key={officialId} type="button" className={`official-assignment-bulk-result ${isSelected ? "is-selected" : ""}`} onClick={() => toggleBulkOfficial(officialId)} disabled={loading || replacing || cancelling}>
                                                        <InitialsAvatar name={official.fullName || official.name} size={28} backgroundColor={colors.primary} textColor={colors.textOnPrimary} />
                                                        <span>{official.fullName || official.name}</span>
                                                        {isSelected && <i className="fas fa-check ml-auto" aria-hidden="true" />}
                                                    </button>
                                                );
                                            }) : <span className="official-assignment-bulk-placeholder">{t("officialAssignmentModal.labels.noBulkOfficialsFound")}</span>}
                                        </div>}
                                        <div className="official-assignment-bulk-selected">
                                            {bulkOfficialIds.length ? bulkOfficialIds.map((officialId) => {
                                                const official = bulkOfficials.find((candidate) => String(candidate.id) === officialId);
                                                if (!official) return null;
                                                return <span key={officialId} className="official-assignment-bulk-chip">{official.fullName || official.name}<button type="button" onClick={() => toggleBulkOfficial(officialId)} aria-label={t("officialAssignmentModal.labels.removeOfficial")}>&times;</button></span>;
                                            }) : <span className="official-assignment-bulk-placeholder">{t("officialAssignmentModal.labels.noBulkOfficialsSelected")}</span>}
                                        </div>
                                        <div className="official-assignment-bulk-review">
                                            <span><strong>{plannedInvitations.length}</strong> {t("officialAssignmentModal.labels.plannedInvitations")}</span>
                                            <span><strong>{skippedBulkOfficialCount}</strong> {t("officialAssignmentModal.labels.notInStagePool")}</span>
                                            <span className={conflictingInvitationCount ? "has-conflicts" : "is-clear"}><strong>{conflictingInvitationCount}</strong> {t("officialAssignmentModal.labels.conflicts")}</span>
                                        </div>
                                        {conflictingInvitationCount > 0 && <div className="official-assignment-bulk-warning"><i className="fas fa-exclamation-triangle mr-1" />{t("officialAssignmentModal.labels.resolveConflicts")}</div>}
                                    </div>
                                    <div className="official-assignment-bulk-actions">
                                        <button type="button" className="btn official-assignment-bulk-button" onClick={applyBulkOfficials} disabled={!bulkOfficialIds.length || loading || replacing || cancelling}>
                                            <i className="fas fa-plus mr-2" />{t("officialAssignmentModal.labels.addBulkOfficials")}
                                        </button>
                                        {Object.keys(lastBulkAdded).length > 0 && <button type="button" className="official-assignment-bulk-undo" onClick={undoBulkOfficials} disabled={loading || replacing || cancelling}>
                                            <i className="fas fa-undo mr-1" />{t("officialAssignmentModal.labels.undoBulkOfficials")}
                                        </button>}
                                    </div>
                                </div>
                                {selectedGames.map((event, gameIndex) => {
                                const eventKey = normalizeEventId(event.id);
                                const eligiblePool = eligibleRefereesByEvent[eventKey] || [];
                                const ineligiblePool = ineligibleRefereesByEvent[eventKey] || [];
                                const currentIds = new Set((event.responsibles || []).map((referee) => String(referee.id)));
                                const options = [
                                    ...eligiblePool,
                                    ...(event.responsibles || []).filter((referee) => !eligiblePool.some((candidate) => String(candidate.id) === String(referee.id))),
                                ];
                                const managers = (event.managers || []).map((manager) => manager.fullName).filter(Boolean);

                                return (
                                    <OfficialAssignmentGameCard key={event.id} event={event} index={gameIndex} colors={colors} t={t} language={language} availableCount={options.length} pickerIcon="fa-user-check" pickerLabel={t("assignmentsPage.officialsToInvite")} selectedCount={(selections[event.id] || []).length} pickerHint={t("officialAssignmentModal.labels.multipleHint")} details={managers.length > 0 && <span className="official-assignment-detail official-assignment-detail-coordinator"><i className="fas fa-user-tie" />{t("officialAssignmentModal.labels.coordinator")}: {managers.join(", ")}</span>}>
                                            <div className="official-assignment-list" role="group" aria-label={t("assignmentsPage.officialsToInvite")}>
                                                {eligibilityLoading ? (
                                                    <div className="official-assignment-empty">{t("common.loading")}</div>
                                                ) : options.length === 0 ? (
                                                    <div className="official-assignment-empty">{t("assignmentsPage.noOfficialsAssigned")}</div>
                                                ) : options.map((referee) => {
                                                    const refereeId = String(referee.id);
                                                    const isSelected = (selections[event.id] || []).includes(refereeId);
                                                    const assignment = assignmentsByEvent[`${event.id}:${refereeId}`];
                                                    const assignmentStatus = assignment?.status?.toLowerCase();
                                                    const hasScheduleConflict = conflictingSelectionKeys.has(`${event.date}:${refereeId}`);
                                                    const canResend = ["invited", "declined", "cancelled"].includes(assignmentStatus);
                                                    const canReplace = assignmentStatus === "accepted";
                                                    const canCancel = ["proposed", "invited", "accepted"].includes(assignmentStatus);
                                                    const unavailableForReplacement = replacementTarget?.eventId === event.id && ["accepted", "completed"].includes(assignmentStatus);
                                                    const resendKey = `${event.id}:${refereeId}`;
                                                    return (
                                                        <label key={referee.id} className={`official-assignment-option ${isSelected ? "is-selected" : ""}`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleSelection(event.id, referee.id)}
                                                                disabled={loading || replacing || cancelling || unavailableForReplacement}
                                                            />
                                                            <span className="official-assignment-check" aria-hidden="true"><i className="fas fa-check" /></span>
                                                            <span className="official-assignment-option-name">{referee.fullName || referee.name}</span>
                                                            {currentIds.has(refereeId) && !assignmentStatus && <span className="official-assignment-current">{t("officialAssignmentModal.labels.current")}</span>}
                                                            {assignmentStatus && (
                                                                <span className={`official-assignment-status is-${assignmentStatus}`}>
                                                                    {t(`officialAssignmentModal.statuses.${assignmentStatus}`)}
                                                                </span>
                                                            )}
                                                            {hasScheduleConflict && (
                                                                <span className="official-assignment-conflict" title={t("officialAssignmentModal.labels.conflictHint")}>
                                                                    <i className="fas fa-exclamation-triangle mr-1" aria-hidden="true" />
                                                                    {t("officialAssignmentModal.labels.conflict")}
                                                                </span>
                                                            )}
                                                            {canResend && (
                                                                <button
                                                                    type="button"
                                                                    className="official-assignment-resend"
                                                                    onClick={(clickEvent) => {
                                                                        clickEvent.preventDefault();
                                                                        clickEvent.stopPropagation();
                                                                        handleResend(event, refereeId);
                                                                    }}
                                                                    disabled={loading || resendingKey === resendKey}
                                                                >
                                                                    <i className="fas fa-redo-alt mr-1" />
                                                                    {resendingKey === resendKey ? t("officialAssignmentModal.sending") : t("officialAssignmentModal.labels.resend")}
                                                                </button>
                                                            )}
                                                            {canReplace && (
                                                                <button
                                                                    type="button"
                                                                    className="official-assignment-replace"
                                                                    onClick={(clickEvent) => {
                                                                        clickEvent.preventDefault();
                                                                        clickEvent.stopPropagation();
                                                                        setReplacementTarget({eventId: event.id, refereeId});
                                                                    }}
                                                                    disabled={loading || replacing}
                                                                >
                                                                    <i className="fas fa-exchange-alt mr-1" />{t("officialAssignmentModal.labels.replace")}
                                                                </button>
                                                            )}
                                                            {canCancel && (
                                                                <button
                                                                    type="button"
                                                                    className="official-assignment-cancel"
                                                                    onClick={(clickEvent) => {
                                                                        clickEvent.preventDefault();
                                                                        clickEvent.stopPropagation();
                                                                        handleCancel(assignment.id);
                                                                    }}
                                                                    disabled={loading || replacing || cancelling}
                                                                >
                                                                    <i className="fas fa-ban mr-1" />{t("assignmentsPage.cancelOfficial")}
                                                                </button>
                                                            )}
                                                        </label>
                                                    );
                                                })}
                                                {ineligiblePool.filter(({referee}) => !currentIds.has(String(referee.id))).map(({referee, reasons}) => (
                                                    <div key={`ineligible-${referee.id}`} className="official-assignment-option official-assignment-option-ineligible">
                                                        <span className="official-assignment-check" aria-hidden="true"><i className="fas fa-lock" /></span>
                                                        <span className="official-assignment-option-name">{referee.fullName || referee.name}</span>
                                                        <span className="official-assignment-conflict" title={reasons.join(" ")}><i className="fas fa-info-circle mr-1" />{reasons[0]}</span>
                                                    </div>
                                                ))}
                                            </div>
                                    </OfficialAssignmentGameCard>
                                );
                                })}
                            </>
                        )}
                    </div>

                    <div className="modal-footer kbola-modalFooter justify-content-between">
                        <span className="text-muted small d-none d-sm-inline">
                            <i className="fas fa-info-circle mr-1" />{t("officialAssignmentModal.labels.stagePoolHint")}
                        </span>
                        <div className="d-flex ml-auto" style={{ gap: 8 }}>
                            <button type="button" className="btn btn-default official-assignment-secondary" data-dismiss="modal">
                                {t("officialAssignmentModal.close")}
                            </button>
                            {replacementTarget && (
                                <button type="button" className="btn official-assignment-secondary" onClick={() => setReplacementTarget(null)} disabled={replacing}>
                                    {t("officialAssignmentModal.labels.cancelReplacement")}
                                </button>
                            )}
                            <button type="button" className="btn btn-primary official-assignment-primary" onClick={replacementTarget ? handleReplace : handleInvite} disabled={loading || replacing || assignmentsLoading || !selectedGames.length || (!replacementTarget && conflictingInvitationCount > 0)} aria-busy={loading || replacing || assignmentsLoading}>
                                <i className={`fas ${replacementTarget ? "fa-exchange-alt" : "fa-paper-plane"} mr-2`} />
                                {replacing ? t("officialAssignmentModal.labels.replacing") : replacementTarget ? t("officialAssignmentModal.labels.confirmReplacement") : loading ? t("officialAssignmentModal.sending") : t("officialAssignmentModal.send")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                #${modalId} .kbola-modalContent {
                    border-radius: 18px;
                    overflow: hidden;
                    border: 1px solid rgba(0,0,0,0.08);
                    box-shadow: 0 18px 44px rgba(0,0,0,0.18);
                    max-height: 90vh;
                    display: flex;
                    flex-direction: column;
                }
                #${modalId} .official-assignment-hero {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 26px 30px;
                }
                #${modalId} .official-assignment-hero-icon {
                    width: 52px;
                    height: 52px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(255,255,255,.2);
                    font-size: 21px;
                    flex: 0 0 auto;
                }
                #${modalId} .official-assignment-hero-copy {
                    min-width: 0;
                }
                #${modalId} .official-assignment-eyebrow {
                    display: block;
                    font-size: 11px;
                    font-weight: 900;
                    letter-spacing: .12em;
                    text-transform: uppercase;
                    opacity: .78;
                    margin-bottom: 5px;
                }
                #${modalId} .official-assignment-hero h4 {
                    font-size: 23px;
                    font-weight: 900;
                    margin: 0 0 5px;
                }
                #${modalId} .official-assignment-hero p {
                    font-size: 13px;
                    line-height: 1.5;
                    opacity: .86;
                    margin: 0;
                    max-width: 620px;
                }
                #${modalId} .official-assignment-close {
                    margin-left: auto;
                    width: 38px;
                    height: 38px;
                    border: 0;
                    border-radius: 10px;
                    background: rgba(255,255,255,.14);
                    color: inherit;
                    font-size: 26px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                }
                #${modalId} .official-assignment-body {
                    padding: 26px 30px;
                    background: #f7f9fc;
                    overflow-y: auto;
                    -webkit-overflow-scrolling: touch;
                }
                #${modalId} .official-assignment-intro {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 13px 15px;
                    margin-bottom: 22px;
                    border: 1px solid #dce9f5;
                    border-radius: 12px;
                    background: #edf7ff;
                    color: #49657f;
                    font-size: 13px;
                    font-weight: 700;
                }
                #${modalId} .official-assignment-intro i {
                    color: #238ac0;
                    font-size: 16px;
                }
                #${modalId} .official-assignment-card {
                    overflow: hidden;
                    padding: 20px !important;
                    border-color: #e1e7ef !important;
                    box-shadow: 0 4px 12px rgba(20,21,26,.035) !important;
                }
                #${modalId} .official-assignment-section-title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 17px;
                    width: 100%;
                    min-width: 0;
                    color: #17202c;
                }
                #${modalId} .official-assignment-section-title > div {
                    min-width: 0;
                    flex: 1 1 auto;
                }
                #${modalId} .official-assignment-section-title > span:first-child {
                    width: 25px;
                    height: 25px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #e8f3fa;
                    color: #1687ba;
                    font-size: 12px;
                    font-weight: 900;
                    flex: 0 0 auto;
                }
                #${modalId} .official-assignment-stage {
                    color: #4b5563;
                    font-size: 12px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: .06em;
                }
                #${modalId} .official-assignment-division {
                    display: inline-flex;
                    align-items: center;
                    margin-right: 7px;
                    padding-right: 7px;
                    border-right: 1px solid #cbd5df;
                    color: #1687ba;
                }
                #${modalId} .official-assignment-game-number {
                    color: #17202c;
                    font-size: 15px;
                    font-weight: 900;
                    margin-top: 2px;
                }
                #${modalId} .official-assignment-available {
                    flex: 0 0 auto;
                    display: inline-flex;
                    align-items: center;
                    border: 1px solid;
                    border-radius: 999px;
                    padding: 5px 10px;
                    font-size: 12px;
                    font-weight: 800;
                    white-space: nowrap;
                }
                #${modalId} .official-assignment-available i {
                    font-size: 11px;
                }
                #${modalId} .official-assignment-available strong {
                    font-weight: 900;
                }
                #${modalId} .official-assignment-matchup .kbola-teamCell {
                    min-width: 0;
                }
                #${modalId} .official-assignment-team {
                    min-width: 0;
                }
                #${modalId} .official-assignment-team-logo {
                    width: 46px;
                    height: 46px;
                    flex: 0 0 46px;
                    object-fit: contain;
                    border-radius: 50%;
                    padding: 4px;
                    background: #f5f7fa;
                    border: 1px solid rgba(0,0,0,.08);
                }
                #${modalId} .official-assignment-team.is-reversed {
                    flex-direction: row-reverse;
                    text-align: right;
                }
                #${modalId} .official-assignment-vs {
                    width: 30px;
                    height: 30px;
                    flex: 0 0 30px;
                    background: #f2f4f7;
                    font-size: 11px;
                    font-weight: 900;
                }
                #${modalId} .official-assignment-matchup,
                #${modalId} .official-assignment-details,
                #${modalId} .official-assignment-picker {
                    margin-left: auto;
                    margin-right: auto;
                }
                #${modalId} .official-assignment-matchup {
                    max-width: 640px;
                }
                #${modalId} .official-assignment-details {
                    justify-content: center;
                }
                #${modalId} .official-assignment-detail {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    max-width: 100%;
                    padding: 7px 10px;
                    border: 1px solid;
                    border-radius: 9px;
                    font-size: 12px;
                    font-weight: 800;
                    line-height: 1.25;
                    white-space: normal;
                    overflow-wrap: anywhere;
                }
                #${modalId} .official-assignment-detail i {
                    width: 14px;
                    flex: 0 0 14px;
                    text-align: center;
                    font-size: 12px;
                }
                #${modalId} .official-assignment-detail-date {
                    color: #1769aa;
                    background: #eef7ff;
                    border-color: #cfe7fa;
                }
                #${modalId} .official-assignment-detail-location {
                    color: #4f6b32;
                    background: #f3f9ed;
                    border-color: #d8eac5;
                }
                #${modalId} .official-assignment-detail-coordinator {
                    color: #67508d;
                    background: #f6f0ff;
                    border-color: #e5d8f7;
                }
                #${modalId} .official-assignment-picker {
                    width: 100%;
                    margin: 8px 0 0;
                }
                #${modalId} .official-assignment-bulk-panel {
                    display: grid;
                    grid-template-columns: minmax(180px, .8fr) minmax(0, 1.8fr) auto;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 14px;
                    padding: 12px;
                    border: 1px solid ${colors.primary}2e;
                    border-radius: 14px;
                    background: linear-gradient(135deg, ${colors.primary}0b, #fff);
                }
                #${modalId} .official-assignment-bulk-copy p {
                    margin: 5px 0 0;
                    color: #667085;
                    font-size: 12px;
                    line-height: 1.45;
                }
                #${modalId} .official-assignment-bulk-picker {
                    min-width: 0;
                }
                #${modalId} .official-assignment-bulk-selected {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 6px;
                    min-height: 32px;
                    margin-top: 6px;
                    padding: 3px 7px;
                    border: 1px solid #d9e1ea;
                    border-radius: 9px;
                    background: #fff;
                }
                #${modalId} .official-assignment-bulk-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 4px 7px 4px 9px;
                    border-radius: 999px;
                    background: ${colors.primary}12;
                    color: ${colors.primary};
                    font-size: 12px;
                    font-weight: 800;
                }
                #${modalId} .official-assignment-bulk-chip button {
                    width: 18px;
                    height: 18px;
                    padding: 0;
                    border: 0;
                    border-radius: 50%;
                    background: transparent;
                    color: inherit;
                    font-size: 16px;
                    line-height: 16px;
                    cursor: pointer;
                }
                #${modalId} .official-assignment-bulk-review {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px 12px;
                    margin-top: 6px;
                    color: #667085;
                    font-size: 11px;
                    font-weight: 700;
                }
                #${modalId} .official-assignment-bulk-review strong {
                    color: #344054;
                    font-size: 13px;
                }
                #${modalId} .official-assignment-bulk-review .has-conflicts,
                #${modalId} .official-assignment-bulk-warning {
                    color: #a33a46;
                }
                #${modalId} .official-assignment-bulk-review .is-clear {
                    color: #16845a;
                }
                #${modalId} .official-assignment-bulk-warning {
                    margin-top: 7px;
                    font-size: 11px;
                    font-weight: 800;
                }
                #${modalId} .official-assignment-bulk-placeholder {
                    color: #98a2b3;
                    font-size: 12px;
                }
                #${modalId} .official-assignment-bulk-search-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                #${modalId} .official-assignment-bulk-search {
                    display: flex;
                    align-items: center;
                    flex: 1;
                    gap: 8px;
                    min-width: 0;
                    padding: 0 10px;
                    border: 1px solid #d9e1ea;
                    border-radius: 8px;
                    background: #fff;
                    color: #98a2b3;
                }
                #${modalId} .official-assignment-bulk-search input {
                    width: 100%;
                    min-height: 34px;
                    border: 0;
                    outline: 0;
                    color: #344054;
                    font-size: 12px;
                }
                #${modalId} .official-assignment-bulk-link {
                    padding: 0;
                    border: 0;
                    background: transparent;
                    color: ${colors.primary};
                    font-size: 11px;
                    font-weight: 900;
                    white-space: nowrap;
                    cursor: pointer;
                }
                #${modalId} .official-assignment-bulk-link.is-muted {
                    color: #667085;
                }
                #${modalId} .official-assignment-bulk-link:disabled {
                    opacity: .45;
                    cursor: not-allowed;
                }
                #${modalId} .official-assignment-bulk-results {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    height: 80px;
                    max-height: 80px;
                    margin-top: 6px;
                    padding: 6px;
                    overflow-y: auto;
                    overscroll-behavior: contain;
                    border: 1px solid #e5eaf0;
                    border-radius: 8px;
                    background: #fbfcfe;
                }
                #${modalId} .official-assignment-bulk-results::-webkit-scrollbar {
                    width: 7px;
                }
                #${modalId} .official-assignment-bulk-results::-webkit-scrollbar-thumb {
                    border-radius: 999px;
                    background: #c8d0da;
                }
                #${modalId} .official-assignment-bulk-result {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    min-width: 0;
                    margin: 0;
                    padding: 8px 9px;
                    width: 100%;
                    border: 0;
                    background: transparent;
                    text-align: left;
                    border-radius: 7px;
                    color: #344054;
                    font-size: 12px;
                    font-weight: 700;
                    cursor: pointer;
                }
                #${modalId} .official-assignment-bulk-result input {
                    width: 16px;
                    height: 16px;
                    flex: 0 0 auto;
                    accent-color: ${colors.primary};
                }
                #${modalId} .official-assignment-bulk-result:hover {
                    background: ${colors.primary}0c;
                }
                #${modalId} .official-assignment-bulk-result.is-selected {
                    background: ${colors.primary}12;
                    color: ${colors.primary};
                }
                #${modalId} .official-assignment-bulk-result span {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                #${modalId} .official-assignment-bulk-button {
                    min-height: 40px;
                    border: 0;
                    border-radius: 9px;
                    background: ${colors.primary};
                    color: ${colors.textOnPrimary};
                    font-size: 12px;
                    font-weight: 900;
                    white-space: nowrap;
                }
                #${modalId} .official-assignment-bulk-button:disabled {
                    opacity: .5;
                    cursor: not-allowed;
                }
                #${modalId} .official-assignment-bulk-actions {
                    display: flex;
                    flex-direction: column;
                    align-items: stretch;
                    gap: 7px;
                }
                #${modalId} .official-assignment-bulk-undo {
                    min-height: 34px;
                    padding: 7px 10px;
                    border: 1px solid #d9dee6;
                    border-radius: 9px;
                    background: #fff;
                    color: #667085;
                    font-size: 11px;
                    font-weight: 900;
                    white-space: nowrap;
                    cursor: pointer;
                }
                #${modalId} .official-assignment-bulk-undo:hover {
                    border-color: #98a2b3;
                    background: #f8fafc;
                    color: #344054;
                }
                #${modalId} .official-assignment-bulk-undo:disabled {
                    opacity: .5;
                    cursor: not-allowed;
                }
                #${modalId} .official-assignment-picker-label {
                    color: #17202c;
                    font-size: 13px;
                    font-weight: 900;
                }
                #${modalId} .official-assignment-list {
                    max-height: 166px;
                    overflow-y: auto;
                    padding: 5px;
                    border: 1px solid #d9e1ea;
                    border-radius: 10px;
                    background: #fbfcfe;
                    box-shadow: inset 0 1px 2px rgba(20,21,26,.025);
                }
                #${modalId} .official-assignment-option {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    min-height: 38px;
                    margin: 0;
                    padding: 6px 9px;
                    border: 1px solid transparent;
                    border-radius: 7px;
                    color: #344054;
                    font-size: 13px;
                    font-weight: 700;
                    cursor: pointer;
                }
                #${modalId} .official-assignment-option:hover {
                    background: #f0f7fb;
                }
                #${modalId} .official-assignment-option.is-selected {
                    border-color: ${colors.primary}35;
                    background: ${colors.primary}0c;
                    color: #17202c;
                }
                #${modalId} .official-assignment-option-ineligible {
                    cursor: not-allowed;
                    opacity: .72;
                    background: #f8fafc;
                }
                #${modalId} .official-assignment-option-ineligible .official-assignment-check {
                    color: #98a2b3;
                    border-color: #d0d5dd;
                    background: #f2f4f7;
                }
                #${modalId} .official-assignment-option input {
                    position: absolute;
                    opacity: 0;
                    pointer-events: none;
                }
                #${modalId} .official-assignment-check {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 18px;
                    height: 18px;
                    flex: 0 0 18px;
                    border: 1px solid #c5ced8;
                    border-radius: 5px;
                    background: #fff;
                    color: #fff;
                    font-size: 10px;
                }
                #${modalId} .official-assignment-option.is-selected .official-assignment-check {
                    border-color: ${colors.primary};
                    background: ${colors.primary};
                }
                #${modalId} .official-assignment-option-name {
                    min-width: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                #${modalId} .official-assignment-current {
                    margin-left: auto;
                    padding: 3px 7px;
                    border-radius: 999px;
                    background: #e9f8f0;
                    color: #16845a;
                    font-size: 10px;
                    font-weight: 900;
                    white-space: nowrap;
                }
                #${modalId} .official-assignment-status {
                    margin-left: 0;
                    padding: 3px 7px;
                    border: 1px solid;
                    border-radius: 999px;
                    font-size: 10px;
                    font-weight: 900;
                    white-space: nowrap;
                }
                #${modalId} .official-assignment-status.is-invited,
                #${modalId} .official-assignment-status.is-proposed {
                    color: #8a5a00;
                    background: #fff7df;
                    border-color: #f1d996;
                }
                #${modalId} .official-assignment-status.is-accepted,
                #${modalId} .official-assignment-status.is-completed {
                    color: #16845a;
                    background: #e9f8f0;
                    border-color: #c8edda;
                }
                #${modalId} .official-assignment-status.is-declined {
                    color: #a33a46;
                    background: #fff0f1;
                    border-color: #f3c9ce;
                }

                #${modalId} .official-assignment-conflict {
                    display: inline-flex;
                    align-items: center;
                    color: #9a5b00;
                    background: #fff7df;
                    border: 1px solid #f1d996;
                    border-radius: 999px;
                    padding: 4px 8px;
                    font-size: 11px;
                    font-weight: 900;
                }
                #${modalId} .official-assignment-resend {
                    margin-left: auto;
                    padding: 4px 8px;
                    border: 1px solid ${colors.primary}45;
                    border-radius: 7px;
                    background: #fff;
                    color: ${colors.primary};
                    font-size: 11px;
                    font-weight: 900;
                    white-space: nowrap;
                    cursor: pointer;
                }
                #${modalId} .official-assignment-resend:hover {
                    background: ${colors.primary}0c;
                }
                #${modalId} .official-assignment-resend:disabled {
                    opacity: .55;
                    cursor: not-allowed;
                }
                #${modalId} .official-assignment-replace {
                    margin-left: auto;
                    padding: 4px 8px;
                    border: 1px solid #d8e0e8;
                    border-radius: 7px;
                    background: #fff;
                    color: #526070;
                    font-size: 11px;
                    font-weight: 900;
                    white-space: nowrap;
                    cursor: pointer;
                }
                #${modalId} .official-assignment-replace:hover {
                    border-color: ${colors.primary};
                    color: ${colors.primary};
                    background: ${colors.primary}0c;
                }
                #${modalId} .official-assignment-replace:disabled {
                    opacity: .55;
                    cursor: not-allowed;
                }
                #${modalId} .official-assignment-cancel {
                    margin-left: auto;
                    padding: 4px 8px;
                    border: 1px solid #edc4c9;
                    border-radius: 7px;
                    background: #fff7f8;
                    color: #a33a46;
                    font-size: 11px;
                    font-weight: 900;
                    white-space: nowrap;
                    cursor: pointer;
                }
                #${modalId} .official-assignment-cancel:hover {
                    border-color: #c95b67;
                    background: #fff0f1;
                }
                #${modalId} .official-assignment-cancel:disabled {
                    opacity: .55;
                    cursor: not-allowed;
                }
                #${modalId} .official-assignment-empty {
                    padding: 16px 10px;
                    color: #7b8794;
                    font-size: 13px;
                    font-weight: 700;
                    text-align: center;
                }
                #${modalId} .official-assignment-list::-webkit-scrollbar {
                    width: 7px;
                }
                #${modalId} .official-assignment-list::-webkit-scrollbar-thumb {
                    border-radius: 8px;
                    background: #cbd5df;
                }
                #${modalId} .official-assignment-select {
                    min-height: 112px;
                    max-height: 150px;
                    overflow-y: auto;
                    resize: vertical;
                    border: 1px solid ${colors.primary}80;
                    border-radius: 10px;
                    padding: 7px 9px;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 1.5;
                }
                #${modalId} .official-assignment-select:focus {
                    border-color: ${colors.primary};
                    box-shadow: 0 0 0 0.2rem ${colors.primary}25;
                }
                #${modalId} .official-assignment-select option {
                    padding: 5px 6px;
                    border-radius: 5px;
                }
                #${modalId} .official-assignment-select option:checked {
                    color: ${colors.textOnPrimary};
                    background: ${colors.primary};
                }
                #${modalId} .official-assignment-primary,
                #${modalId} .official-assignment-secondary {
                    border-radius: 10px;
                    padding: 10px 17px;
                    font-weight: 900;
                }
                #${modalId} .official-assignment-secondary {
                    border: 1px solid #d8e0e8;
                    background: #fff;
                    color: #526070;
                }
                #${modalId} .official-assignment-primary {
                    border: 0;
                    box-shadow: 0 7px 16px rgba(20,21,26,.13);
                }
                #${modalId} .kbola-modalFooter {
                    padding: 17px 30px;
                    border-top: 1px solid #e5eaf0;
                    background: #fff;
                    gap: 10px;
                }
                @media (max-width: 767px) {
                    #${modalId} .official-assignment-hero {
                        padding: 21px 18px;
                    }
                    #${modalId} .official-assignment-hero h4 {
                        font-size: 19px;
                    }
                    #${modalId} .official-assignment-hero p {
                        font-size: 12px;
                    }
                    #${modalId} .official-assignment-body {
                        padding: 19px 18px;
                    }
                    #${modalId} .official-assignment-card {
                        padding: 16px !important;
                    }
                    #${modalId} .kbola-modalFooter {
                        padding: 14px 18px;
                    }
                    #${modalId} .official-assignment-picker {
                        max-width: 100%;
                    }
                    #${modalId} .official-assignment-bulk-panel {
                        grid-template-columns: 1fr;
                        gap: 11px;
                    }
                    #${modalId} .official-assignment-bulk-search-row {
                        flex-wrap: wrap;
                    }
                    #${modalId} .official-assignment-bulk-search {
                        flex-basis: 100%;
                    }
                    #${modalId} .official-assignment-bulk-button {
                        width: 100%;
                    }
                    #${modalId} .modal-footer {
                        align-items: stretch;
                    }
                    #${modalId} .official-assignment-primary,
                    #${modalId} .official-assignment-secondary {
                        flex: 1;
                    }
                }
            `}</style>
        </div>
    );
}
